// src/hooks/useTimeBoxManager.ts
/**
 * TimeBox 管理 Hook
 *
 * 處理 TimeBox 的 CRUD 操作和模態框控制
 * 從 Today.tsx 抽取，保持功能完整性
 */
import { useState, useCallback } from 'react';
import { TimeBox, TimeBoxCategory, CreateTimeBoxRequest } from '../../../types/timeBox';
import { createTimeBox, updateTimeBox, deleteTimeBox } from '../../../services/api/timeBoxes';
import {
  deleteTimeBoxLocal,
  createTimeBoxLocal,
  updateTimeBoxLocal,
  replaceTimeBoxTempId
} from '../../../services/indexedDB';
import { addToSyncQueue, updatePendingCreateTimeBox, removeSyncQueueItemByTargetId } from '../../../services/syncQueueService';
import { eventBus, EVENTS } from '../../../utils/eventBus';
import { logger } from '../../../utils/logger';

export interface UseTimeBoxManagerOptions {
  userId: string;
  onRefresh: () => void;
  defaultCategory?: TimeBoxCategory;
}

export interface UseTimeBoxManagerReturn {
  // 模態框狀態
  showTimeBoxModal: boolean;
  selectedTimeBoxId: string | null;  // 改為只保存 ID
  selectedTimeBox: TimeBox | null;    // 向後兼容，已廢棄
  isSubmittingTimeBox: boolean;       // 向後兼容，已廢棄
  modalMode: 'create' | 'edit';
  // 方法
  openTimeBoxModal: (timeBox: TimeBox, mode?: 'create' | 'edit') => void;  // 向後兼容
  openTimeBoxDetail: (timeBoxId: string, mode?: 'create' | 'edit') => void;  // 新方法
  closeTimeBoxModal: () => void;
  createNewTimeBox: (startTime?: Date, endTime?: Date) => TimeBox;
  handleSaveTimeBox: (timeBox: TimeBox) => Promise<void>;      // 向後兼容，已廢棄
  handleDeleteTimeBox: (timeBoxId: string) => Promise<void>;   // 向後兼容，已廢棄
  handleTimeBoxAdjust: (timeBoxId: string, timeBoxes: TimeBox[]) => void;
}

export function useTimeBoxManager(options: UseTimeBoxManagerOptions): UseTimeBoxManagerReturn {
  const { userId, onRefresh, defaultCategory = 'focus' } = options;

  // 模態框狀態
  const [showTimeBoxModal, setShowTimeBoxModal] = useState<boolean>(false);
  const [selectedTimeBoxId, setSelectedTimeBoxId] = useState<string | null>(null);  // 新：只保存 ID
  const [selectedTimeBox, setSelectedTimeBox] = useState<TimeBox | null>(null);  // 舊：向後兼容
  const [isSubmittingTimeBox, setIsSubmittingTimeBox] = useState<boolean>(false);  // 舊：向後兼容
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  /**
   * 創建新的時間箱模板
   */
  const createNewTimeBoxTemplate = useCallback((startTime?: Date, endTime?: Date): TimeBox => {
    const now = new Date();

    // 設置開始時間為當前時間，向上取整到最近的15分鐘
    const start = startTime || new Date(now);
    if (!startTime) {
      start.setMinutes(Math.ceil(now.getMinutes() / 15) * 15);
      start.setSeconds(0);
      start.setMilliseconds(0);
    }

    // 設置結束時間為開始時間後1小時
    const end = endTime || new Date(start);
    if (!endTime) {
      end.setHours(end.getHours() + 1);
    }

    return {
      id: `temp-${Date.now()}`,
      itemType: 'timebox',
      userId: userId,
      title: '',
      category: defaultCategory,
      startTime: start,
      endTime: end,
      status: 'normal',
      createdAt: now,
    };
  }, [userId, defaultCategory]);

  /**
   * 打開時間箱模態框
   */
  const openTimeBoxModal = useCallback((timeBox: TimeBox, mode: 'create' | 'edit' = 'edit') => {
    setSelectedTimeBox(timeBox);
    setModalMode(mode);
    setShowTimeBoxModal(true);
  }, []);

  /**
   * 打開時間箱詳情（新方法）
   */
  const openTimeBoxDetail = useCallback((timeBoxId: string, mode: 'create' | 'edit' = 'edit') => {
    setSelectedTimeBoxId(timeBoxId);
    setModalMode(mode);
    setShowTimeBoxModal(true);
  }, []);

  /**
   * 關閉時間箱模態框
   */
  const closeTimeBoxModal = useCallback(() => {
    setShowTimeBoxModal(false);
    setSelectedTimeBox(null);
    setSelectedTimeBoxId(null);
  }, []);

  /**
   * 創建新的時間箱並打開模態框
   */
  const createNewTimeBox = useCallback((startTime?: Date, endTime?: Date): TimeBox => {
    const newTimeBox = createNewTimeBoxTemplate(startTime, endTime);
    openTimeBoxModal(newTimeBox, 'create');
    return newTimeBox;
  }, [createNewTimeBoxTemplate, openTimeBoxModal]);

  /**
   * 保存時間箱（離線優先）
   */
  const handleSaveTimeBox = useCallback(async (timeBox: TimeBox) => {
    setIsSubmittingTimeBox(true);
    try {
      const isNewTimeBox = timeBox.id.startsWith('temp');

      // 只有當 ID 是臨時的且模式是 'create' 時，才視為新建
      // 否則，即使 ID 是臨時的（但模式是 'edit'），也視為更新（離線創建後編輯的情況）
      if (isNewTimeBox && modalMode === 'create') {
        // === 新建 TimeBox ===

        // 1. 立即保存到 IndexedDB
        await createTimeBoxLocal(timeBox);
        logger.info('TimeBoxManager', 'TimeBox 已保存到 IndexedDB', { timeBoxId: timeBox.id });

        // 2. 嘗試同步到雲端
        const currentlyOnline = navigator.onLine;

        if (currentlyOnline) {
          try {
            // 線上：嘗試同步到伺服器
            const timeBoxRequest: CreateTimeBoxRequest = {
              itemType: 'timebox',
              userId: timeBox.userId || userId,
              title: timeBox.title || '',
              notes: timeBox.notes || timeBox.description || '',
              startTime: timeBox.startTime,
              endTime: timeBox.endTime,
              category: timeBox.category,
              status: timeBox.status || 'normal',
              ...(timeBox.location ? { location: timeBox.location } : {}),
              ...(timeBox.taskId ? { taskId: timeBox.taskId } : {})
            };

            const result = await createTimeBox(timeBoxRequest);

            // 成功：替換臨時 ID 為真實 ID
            await replaceTimeBoxTempId(timeBox.id, result.id);
            logger.info('TimeBoxManager', 'TimeBox 已同步到伺服器', {
              tempId: timeBox.id,
              realId: result.id
            });

            eventBus.publish(EVENTS.TIMEBOX.CREATED, result);
          } catch (syncError) {
            // 同步失敗：加入同步佇列
            logger.warn('TimeBoxManager', 'TimeBox 伺服器同步失敗，加入同步佇列', {
              timeBoxId: timeBox.id,
              error: syncError
            });
            await addToSyncQueue('CREATE_TIMEBOX', timeBox.id, timeBox);
            eventBus.publish(EVENTS.TIMEBOX.CREATED, timeBox);
          }
        } else {
          // 離線：直接加入同步佇列
          logger.info('TimeBoxManager', '離線模式：TimeBox 加入同步佇列', {
            timeBoxId: timeBox.id
          });
          await addToSyncQueue('CREATE_TIMEBOX', timeBox.id, timeBox);
          eventBus.publish(EVENTS.TIMEBOX.CREATED, timeBox);
        }
      } else {
        // === 更新現有 TimeBox (無論是真實 ID 還是臨時 ID) ===

        // 1. 立即更新 IndexedDB
        await updateTimeBoxLocal(timeBox.id, timeBox);
        logger.info('TimeBoxManager', 'TimeBox 已在 IndexedDB 更新', { timeBoxId: timeBox.id });

        // 2. 嘗試同步到雲端
        const currentlyOnline = navigator.onLine;

        if (currentlyOnline) {
          // 如果還是 temp ID 但是在庫裡，說明之前的創建可能失敗了或者還沒完成
          // 對於 temp ID，我們只更新隊列，不直接發起 UPDATE 請求到 API (因為沒有真實 ID)
          if (isNewTimeBox) {
            logger.info('TimeBoxManager', '更新未同步的本地 TimeBox', { timeBoxId: timeBox.id });
            await updatePendingCreateTimeBox(timeBox.id, timeBox);
          } else {
            try {
              // 線上：嘗試同步到伺服器
              await updateTimeBox({
                id: timeBox.id,
                title: timeBox.title || '',
                notes: timeBox.notes || timeBox.description || '',
                startTime: timeBox.startTime,
                endTime: timeBox.endTime,
                category: timeBox.category,
                ...(timeBox.location !== undefined ? { location: timeBox.location } : {}),
                ...(timeBox.taskId !== undefined ? { taskId: timeBox.taskId } : {})
              });
              logger.info('TimeBoxManager', 'TimeBox 已同步到伺服器', { timeBoxId: timeBox.id });
            } catch (syncError) {
              // 同步失敗：加入同步佇列
              logger.warn('TimeBoxManager', 'TimeBox 伺服器同步失敗，加入同步佇列', {
                timeBoxId: timeBox.id,
                error: syncError
              });
              await addToSyncQueue('UPDATE_TIMEBOX', timeBox.id, timeBox);
            }
          }
        } else {
          // 離線：直接加入同步佇列
          if (isNewTimeBox) {
            // 對於還未同步的 temp TimeBox，我們更新原有的 CREATE 任務
            logger.info('TimeBoxManager', '離線模式：更新未同步的 TimeBox', {
              timeBoxId: timeBox.id
            });
            await updatePendingCreateTimeBox(timeBox.id, timeBox);
          } else {
            logger.info('TimeBoxManager', '離線模式：TimeBox 更新加入同步佇列', {
              timeBoxId: timeBox.id
            });
            await addToSyncQueue('UPDATE_TIMEBOX', timeBox.id, timeBox);
          }
        }

        eventBus.publish(EVENTS.TIMEBOX.UPDATED, timeBox.id, timeBox);
      }

      // 3. 刷新 UI 並關閉模態框
      onRefresh();
      closeTimeBoxModal();
    } catch (error) {
      logger.error('TimeBoxManager', '保存時間箱失敗', { error });
      throw error;
    } finally {
      setIsSubmittingTimeBox(false);
    }
  }, [userId, modalMode, onRefresh, closeTimeBoxModal]);

  /**
   * 刪除時間箱（離線優先）
   */
  const handleDeleteTimeBox = useCallback(async (timeBoxId: string) => {
    setIsSubmittingTimeBox(true);
    try {
      // 臨時 TimeBox 直接從 IndexedDB 刪除即可
      if (timeBoxId.startsWith('temp')) {
        // 先嘗試從同步佇列中移除任何待處理的創建任務 (防止鬼影數據)
        try {
          await removeSyncQueueItemByTargetId('CREATE_TIMEBOX', timeBoxId);
          logger.info('TimeBoxManager', '已移除 pending CREATE_TIMEBOX 任務', { timeBoxId });
        } catch (err) {
          logger.warn('TimeBoxManager', '嘗試移除 pending 任務失敗 (非致命)', { error: err });
        }

        await deleteTimeBoxLocal(timeBoxId);
        eventBus.publish(EVENTS.TIMEBOX.DELETED, timeBoxId);
        onRefresh();
        closeTimeBoxModal();
        return;
      }

      // 1. 立即從 IndexedDB 刪除
      await deleteTimeBoxLocal(timeBoxId);
      logger.info('TimeBoxManager', 'TimeBox 已從 IndexedDB 刪除', { timeBoxId });

      // 2. 嘗試從伺服器刪除
      const currentlyOnline = navigator.onLine;

      if (currentlyOnline) {
        try {
          // 線上：嘗試同步刪除到伺服器
          await deleteTimeBox(timeBoxId);
          logger.info('TimeBoxManager', 'TimeBox 已從伺服器刪除', { timeBoxId });
        } catch (syncError) {
          logger.warn('TimeBoxManager', 'TimeBox 伺服器刪除失敗，加入同步佇列', { timeBoxId, error: syncError });
          // 同步失敗，加入佇列
          await addToSyncQueue('DELETE_TIMEBOX', timeBoxId);
        }
      } else {
        // 離線：直接加入同步佇列
        logger.info('TimeBoxManager', '離線模式：TimeBox 刪除加入同步佇列', { timeBoxId });
        await addToSyncQueue('DELETE_TIMEBOX', timeBoxId);
      }

      // 3. 發布刪除事件並刷新
      eventBus.publish(EVENTS.TIMEBOX.DELETED, timeBoxId);
      onRefresh();
      closeTimeBoxModal();
    } catch (error) {
      logger.error('TimeBoxManager', '刪除時間箱失敗', { error });
      throw error;
    } finally {
      setIsSubmittingTimeBox(false);
    }
  }, [onRefresh, closeTimeBoxModal]);

  /**
   * 處理時間箱調整 - 使用模態框編輯
   * 更新：現在使用 openTimeBoxDetail（只傳 ID）
   */
  const handleTimeBoxAdjust = useCallback((timeBoxId: string, timeBoxes: TimeBox[]) => {
    // 新方法：直接使用 ID
    openTimeBoxDetail(timeBoxId, 'edit');
  }, [openTimeBoxDetail]);

  return {
    showTimeBoxModal,
    selectedTimeBoxId,      // 新
    selectedTimeBox,        // 向後兼容
    isSubmittingTimeBox,    // 向後兼容
    modalMode,
    openTimeBoxModal,       // 向後兼容
    openTimeBoxDetail,      // 新
    closeTimeBoxModal,
    createNewTimeBox,
    handleSaveTimeBox,      // 向後兼容
    handleDeleteTimeBox,    // 向後兼容
    handleTimeBoxAdjust,
  };
}
